import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import UserAvatar from '@/components/UserAvatar';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Phone, 
  Shield, 
  LogOut,
  Edit2,
  Save,
  X,
  Settings,
  HelpCircle,
  ChevronRight,
  Star,
  AlertTriangle,
  TrendingUp,
  LayoutDashboard,
  Crown,
  CheckCircle,
  Copy,
  Gift,
  Lock,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [credibility, setCredibility] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || ''
  });

  useEffect(() => {
    const fetchCredibility = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/credibility`, {
          headers: getAuthHeaders()
        });
        setCredibility(response.data);
      } catch (error) {
        console.error('Failed to fetch credibility:', error);
      }
    };
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subscription/me`, {
          headers: getAuthHeaders()
        });
        setSubscription(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };
    const fetchReferralInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/referral/info`, { headers: getAuthHeaders() });
        setReferralInfo(res.data);
      } catch (error) {
        console.error('Failed to fetch referral info:', error);
      }
    };
    const refreshUser = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`, { headers: getAuthHeaders() });
        setUser(res.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    };
    if (user) {
      refreshUser();
      fetchCredibility();
      fetchSubscription();
      fetchReferralInfo();
    }
  }, [user, getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/profile`, formData, {
        headers: getAuthHeaders()
      });
      setUser(response.data);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const copyReferralCode = () => {
    if (referralInfo?.referral_code) {
      navigator.clipboard.writeText(referralInfo.referral_code);
      toast.success('Referral code copied!');
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCodeInput.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplyingReferral(true);
    try {
      const res = await axios.post(`${API_URL}/api/referral/apply`, {
        code: referralCodeInput.trim()
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setReferralCodeInput('');
      const updated = await axios.get(`${API_URL}/api/referral/info`, { headers: getAuthHeaders() });
      setReferralInfo(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplyingReferral(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const formPayload = new FormData();
    formPayload.append('file', file);
    try {
      await axios.post(`${API_URL}/api/auth/avatar`, formPayload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      setAvatarKey(Date.now());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload picture');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="profile-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Profile</h1>
          {user?.is_admin && (
            <Button 
              variant="outline"
              onClick={() => navigate('/admin')}
              className="rounded-full"
              data-testid="admin-button"
            >
              <Settings size={16} className="mr-1" />
              Admin
            </Button>
          )}
        </div>

        {/* Profile Card */}
        <Card className="glass-card mb-6" data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative group">
                <UserAvatar key={avatarKey} userId={user?.id} name={user?.full_name || ''} size={64} />
                <label className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center cursor-pointer transition-colors">
                  <Edit2 size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-upload" />
                </label>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-testid="profile-name">
                  {user?.full_name}
                  {user?.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="verified-badge">
                      <CheckCircle size={12} /> Verified
                    </span>
                  )}
                </h2>
                <p className="text-sm text-slate-500">{user?.email}</p>
                {user?.is_admin && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700 mt-1">
                    Admin
                  </span>
                )}
              </div>
            </div>

            <Separator className="mb-6" />

            {editing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="h-12 rounded-xl bg-slate-50"
                    data-testid="edit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12 rounded-xl bg-slate-50"
                    data-testid="edit-phone-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                    data-testid="save-profile-button"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(false)}
                    className="h-12 rounded-xl"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="p-2 rounded-lg bg-white">
                    <User size={16} className="text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Full Name</p>
                    <p className="font-medium text-slate-900">{user?.full_name}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="p-2 rounded-lg bg-white">
                    <Mail size={16} className="text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{user?.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="p-2 rounded-lg bg-white">
                    <Phone size={16} className="text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">{user?.phone || 'Not provided'}</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="w-full h-12 rounded-xl"
                  data-testid="edit-profile-button"
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Plan Card */}
        {subscription && (
          <Card className="glass-card mb-6" data-testid="subscription-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${
                    subscription.subscription.plan_type === 'pro' ? 'bg-gradient-to-br from-violet-600 to-purple-700' :
                    subscription.subscription.plan_type === 'premium' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                    'bg-gradient-to-br from-slate-500 to-slate-700'
                  }`}>
                    <Crown size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Current Plan</p>
                    <p className="font-bold text-slate-900 capitalize">{subscription.subscription.plan_type}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="rounded-full text-xs"
                  data-testid="view-plans-btn"
                >
                  {subscription.subscription.plan_type === 'free' ? 'Upgrade' : 'Manage'}
                  <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-xl bg-slate-50">
                  <p className="text-[10px] text-slate-500">Created</p>
                  <p className="text-sm font-bold text-slate-900">
                    {subscription.usage.groups_created}/{subscription.usage.max_create === -1 ? '\u221e' : subscription.usage.max_create}
                  </p>
                </div>
                <div className="p-2 rounded-xl bg-slate-50">
                  <p className="text-[10px] text-slate-500">Joined</p>
                  <p className="text-sm font-bold text-slate-900">
                    {subscription.usage.groups_joined}/{subscription.usage.max_join === -1 ? '\u221e' : subscription.usage.max_join}
                  </p>
                </div>
                <div className="p-2 rounded-xl bg-slate-50">
                  <p className="text-[10px] text-slate-500">Goals</p>
                  <p className="text-sm font-bold text-slate-900">
                    {subscription.usage.savings_goals_active}/{subscription.usage.max_savings_goals === -1 ? '\u221e' : subscription.usage.max_savings_goals}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KYC Verification Card */}
        {!user?.is_verified && (
          <Card className="glass-card mb-6 border-2 border-amber-200" data-testid="kyc-prompt-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 rounded-xl bg-amber-100">
                  <Shield size={18} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#0B1F3A]">Verify Your Account</p>
                  <p className="text-xs text-slate-500">Unlock full access and join trusted groups</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/kyc')}
                className="w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-bold"
                data-testid="verify-account-button"
              >
                <Shield size={16} className="mr-2" />
                Verify Account
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Referral Card */}
        {referralInfo && (
          <Card className="glass-card mb-6" data-testid="referral-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl bg-[#2272C5]/10">
                  <Gift size={18} className="text-[#2272C5]" />
                </div>
                <div>
                  <p className="font-bold text-[#0B1F3A]">Refer & Earn</p>
                  <p className="text-xs text-slate-500">Earn up to {'\u20a6'}5,000 per referral</p>
                </div>
              </div>

              {/* Your referral code */}
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-slate-500">Your Referral Code</p>
                  <p className="font-black text-[#0B1F3A] text-lg tracking-wider" data-testid="referral-code">{referralInfo.referral_code}</p>
                </div>
                <Button variant="outline" size="sm" onClick={copyReferralCode} className="rounded-xl" data-testid="copy-referral">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>

              {/* WhatsApp Share */}
              <Button
                onClick={() => {
                  const msg = encodeURIComponent(`Join me on AegisPay! Save smarter with group savings. Use my referral code: ${referralInfo.referral_code}\n\nDownload: https://app.aegispay.app`);
                  window.open(`https://wa.me/?text=${msg}`, '_blank');
                }}
                className="w-full mb-3 h-11 bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl font-semibold text-sm"
                data-testid="whatsapp-share-btn"
              >
                Share on WhatsApp
              </Button>

              {/* Referral stats */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-2 rounded-xl bg-slate-50 text-center">
                  <p className="text-[10px] text-slate-500">Referrals</p>
                  <p className="font-bold text-[#0B1F3A]">{referralInfo.total_referrals}</p>
                </div>
                <div className="p-2 rounded-xl bg-amber-50 text-center">
                  <p className="text-[10px] text-amber-600">Locked</p>
                  <p className="font-bold text-amber-700">{referralInfo.locked_bonuses}</p>
                </div>
                <div className="p-2 rounded-xl bg-emerald-50 text-center">
                  <p className="text-[10px] text-emerald-600">Earned</p>
                  <p className="font-bold text-emerald-700">{'\u20a6'}{referralInfo.total_earned.toLocaleString()}</p>
                </div>
              </div>

              {/* Apply referral code */}
              {!user?.referred_by && (
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2 font-semibold">Have a referral code?</p>
                  <div className="flex gap-2">
                    <Input
                      value={referralCodeInput}
                      onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="h-10 rounded-xl flex-1 uppercase"
                      data-testid="apply-referral-input"
                    />
                    <Button
                      onClick={handleApplyReferral}
                      disabled={applyingReferral}
                      size="sm"
                      className="h-10 rounded-xl bg-[#2272C5]"
                      data-testid="apply-referral-button"
                    >
                      Apply
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Bonus unlocked after completing your first savings cycle.</p>
                </div>
              )}
              {user?.referred_by && (
                <div className="pt-3 border-t border-slate-200 flex items-center gap-2">
                  <Lock size={12} className="text-amber-500" />
                  <p className="text-xs text-slate-500">Referral bonus: {'\u20a6'}5,000 (locked until first cycle completion)</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Credibility Score Card */}
        <Card className="glass-card mb-6" data-testid="credibility-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2">
              <Star size={18} className="text-amber-500" />
              Credibility Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Score Display */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    (credibility?.credibility_score || 100) >= 70 
                      ? 'bg-emerald-100' 
                      : (credibility?.credibility_score || 100) >= 50 
                        ? 'bg-amber-100' 
                        : 'bg-red-100'
                  }`}>
                    <span className={`text-2xl font-bold ${
                      (credibility?.credibility_score || 100) >= 70 
                        ? 'text-emerald-600' 
                        : (credibility?.credibility_score || 100) >= 50 
                          ? 'text-amber-600' 
                          : 'text-red-600'
                    }`}>
                      {credibility?.credibility_score || 100}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p className={`font-semibold ${
                      (credibility?.credibility_score || 100) >= 70 
                        ? 'text-emerald-600' 
                        : (credibility?.credibility_score || 100) >= 50 
                          ? 'text-amber-600' 
                          : 'text-red-600'
                    }`}>
                      {credibility?.status || 'Excellent'}
                    </p>
                  </div>
                </div>
                <TrendingUp size={24} className="text-slate-300" />
              </div>

              {/* Progress Bar */}
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    (credibility?.credibility_score || 100) >= 70 
                      ? 'bg-emerald-500' 
                      : (credibility?.credibility_score || 100) >= 50 
                        ? 'bg-amber-500' 
                        : 'bg-red-500'
                  }`}
                  style={{ width: `${credibility?.credibility_score || 100}%` }}
                />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className="text-amber-500" />
                    <p className="text-xs text-slate-500">Missed Payments</p>
                  </div>
                  <p className="text-lg font-bold text-slate-900">
                    {credibility?.missed_payments || 0}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield size={14} className="text-red-500" />
                    <p className="text-xs text-slate-500">Total Penalties</p>
                  </div>
                  <p className="text-lg font-bold text-slate-900">
                    ₦{(credibility?.total_penalties || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Info Text */}
              <p className="text-xs text-slate-500 mt-2">
                Your credibility score affects your ability to join groups. Maintain on-time payments to keep your score high.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="glass-card mb-6" data-testid="account-info-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#0B1F3A]">Account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <Shield size={16} className="text-slate-500" />
                  </div>
                  <span className="text-slate-700">Member since</span>
                </div>
                <span className="text-sm text-slate-500">{formatDate(user?.created_at)}</span>
              </div>
              <button 
                onClick={() => navigate('/help')}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors"
                data-testid="help-link"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#2272C5]/10">
                    <HelpCircle size={16} className="text-[#2272C5]" />
                  </div>
                  <span className="text-slate-700">Help & FAQs</span>
                </div>
                <ChevronRight size={16} className="text-slate-400" />
              </button>

              {/* Admin Dashboard Link - Only visible for admins */}
              {user?.is_admin && (
                <button 
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] transition-colors mt-2"
                  data-testid="admin-dashboard-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/20">
                      <LayoutDashboard size={16} className="text-white" />
                    </div>
                    <span className="text-white font-semibold">Admin Dashboard</span>
                  </div>
                  <ChevronRight size={16} className="text-white/70" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Demo Notice */}
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-amber-100">
              <Shield size={16} className="text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-900">Demo Account</p>
              <p className="text-sm text-amber-700 mt-1">
                This is a demo environment. All funds and transactions are simulated for testing purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full h-14 rounded-2xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-button"
        >
          <LogOut size={18} className="mr-2" />
          Log Out
        </Button>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
